import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { CustomerService } from '../../../../services/customer.service';
import { Customer } from '../../../../models/customer.model';
import { RevenueTypeService } from '../../../../services/revenueType.service';
import { RevenueType } from '../../../../models/revenueType.model';
import { BankAccountService } from '../../../../services/bankAccount.service';
import { BankAccount } from '../../../../models/bankAccount.model';
import { FinancialService } from '../../../../services/financial.service';
import { CreateFinancialDto } from '../../../../models/dtos/createFinancial.dto';
import { TypePaymentService } from '../../../../services/typePayment.service';
import { TypePayment } from '../../../../models/typePayment.model';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { addMonths } from 'date-fns';

@Component({
  selector: 'app-form-revenue',
  standalone: true,
  templateUrl: './form-revenue.component.html',
  styleUrls: ['./form-revenue.component.css'],
  imports: [
    CommonModule, 
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,DropdownModule, CalendarModule, InputNumberModule, CheckboxModule, ButtonModule,CardModule,TextareaModule,
    DialogModule,
    TableModule,
  ],
  providers: [CustomerService, RevenueTypeService, BankAccountService, FinancialService, TypePaymentService]
})
export class FormRevenueComponent implements OnInit {
  form!: FormGroup;
  isEdit = false;
  customers: Customer[] = [];
  customerDropdownOptions: any[] = [];
  customerSearchTerm: string = '';
  private customerFilterTimer: any;
  revenueTypes: RevenueType[] = [];
  revenueTypeDropdownOptions: any[] = [];
  bankAccounts: BankAccount[] = [];
  typePayments: TypePayment[] = [];
  typePaymentDropdownOptions: any[] = [];

  // Select options
  occurrenceOptions = ['Única', 'Parcelada/Recorrente'];
  installmentIntervalOptions = [
    { label: 'Quinzenal', value: 'quinzenal' },
    { label: 'Mensal', value: 'mensal' },
    { label: 'Semestral', value: 'semestral' },
    { label: 'Anual', value: 'anual' },
    { label: 'Personalizado (dias)', value: 'custom' }
  ];
  installmentStatusOptions = [
    { label: 'Quitado', value: 'quitado' },
    { label: 'Lançamento Futuro', value: 'futuro' }
  ];
  installments: any[] = [];

  showParcelModal = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private revenueTypeService: RevenueTypeService,
    private bankAccountService: BankAccountService,
    private financialService: FinancialService,
    private typePaymentService: TypePaymentService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    this.form = this.fb.group({
      isPaid: [false, Validators.required],
      description: ['', Validators.required],
      customer: ['', Validators.required],
      revenueType: ['', Validators.required],
      occurrence: ['Única', Validators.required],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      dueDate: [nextMonth, Validators.required],
      bankAccount: ['', Validators.required],
      paymentMethod: ['', Validators.required],
      interest: [null, [Validators.required, Validators.min(0)]],
      fine: [null, [Validators.required, Validators.min(0)]],
      barcode: ['', Validators.required],
      notes: [''],
      // Novos campos para recorrência/parcelamento
      installmentInterval: ['mensal'],
      customIntervalDays: [{ value: '', disabled: true }],
      installmentCount: [2],
      firstInstallmentDate: ['']
    });

    // Pré-seleciona cliente vindo da tela de visualização
    const customerIdFromQuery = this.route.snapshot.queryParamMap.get('customerId');
    if (customerIdFromQuery) {
      const idNum = Number(customerIdFromQuery);
      this.customerService.getById(idNum).subscribe({
        next: (resp) => {
          const cust = resp?.data;
          if (cust) {
            // garante exibição correta no dropdown
            this.customerDropdownOptions = [
              { label: 'Selecione um cliente', value: '' },
              { label: cust.fullName, value: cust.id }
            ];
            this.form.get('customer')?.setValue(cust.id);
          } else {
            this.form.get('customer')?.setValue(idNum);
          }
        },
        error: () => {
          this.form.get('customer')?.setValue(idNum);
        }
      });
    }

    // Inicializar arrays de dropdown
    this.customerDropdownOptions = [{ label: 'Selecione um cliente', value: '' }];
    this.revenueTypeDropdownOptions = [{ label: 'Selecione um tipo de receita', value: '' }];
    this.typePaymentDropdownOptions = [{ label: 'Selecione um método de pagamento', value: '' }];

    // Não carregar todos os clientes aqui para evitar travar; o usuário pesquisa quando precisar
    this.loadRevenueTypes();
    this.loadBankAccounts();
    this.loadTypePayments();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEdit = true;
      console.log('Editando receita com ID:', id);
    }

    this.form.get('occurrence')?.valueChanges.subscribe(val => {
      if (val === 'Parcelada/Recorrente') {
        this.form.get('installmentInterval')?.setValidators([Validators.required]);
        this.form.get('installmentCount')?.setValidators([Validators.required, Validators.min(2)]);
        this.form.get('firstInstallmentDate')?.setValidators([Validators.required]);
      } else {
        this.form.get('installmentInterval')?.clearValidators();
        this.form.get('installmentCount')?.clearValidators();
        this.form.get('firstInstallmentDate')?.clearValidators();
        this.installments = [];
      }
      this.form.get('installmentInterval')?.updateValueAndValidity();
      this.form.get('installmentCount')?.updateValueAndValidity();
      this.form.get('firstInstallmentDate')?.updateValueAndValidity();
    });
    this.form.get('installmentInterval')?.valueChanges.subscribe(val => {
      if (val === 'custom') {
        this.form.get('customIntervalDays')?.enable();
      } else {
        this.form.get('customIntervalDays')?.disable();
        this.form.get('customIntervalDays')?.setValue('');
      }
      this.generateInstallments();
    });
    this.form.get('installmentCount')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('firstInstallmentDate')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('amount')?.valueChanges.subscribe(() => this.generateInstallments());
    this.form.get('customIntervalDays')?.valueChanges.subscribe(() => this.generateInstallments());
  }

  generateInstallments() {
    if (this.form.get('occurrence')?.value !== 'Parcelada/Recorrente') {
      this.installments = [];
      return;
    }
    const count = Number(this.form.get('installmentCount')?.value);
    const firstDate = this.form.get('firstInstallmentDate')?.value;
    const total = Number(this.form.get('amount')?.value);
    const interval = this.form.get('installmentInterval')?.value;
    const customDays = Number(this.form.get('customIntervalDays')?.value);
    if (!count || !firstDate || !total || !interval || (interval === 'custom' && !customDays)) {
      this.installments = [];
      return;
    }
    const valuePerInstallment = +(total / count).toFixed(2);
    let currentDate = new Date(firstDate);
    this.installments = [];
    for (let i = 0; i < count; i++) {
      this.installments.push({
        date: new Date(currentDate),
        amount: valuePerInstallment,
        paid: false,
        paymentMethod: this.form.get('paymentMethod')?.value || ''
      });
      switch (interval) {
        case 'quinzenal':
          currentDate.setDate(currentDate.getDate() + 15);
          break;
        case 'mensal':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'semestral':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;
        case 'anual':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
        case 'custom':
          currentDate.setDate(currentDate.getDate() + customDays);
          break;
      }
    }
  }

  onInstallmentPaymentMethodChange(startIndex: number, newValue: any): void {
    // Aplica a partir da parcela alterada, sem afetar as anteriores
    for (let i = startIndex; i < this.installments.length; i++) {
      this.installments[i].paymentMethod = newValue;
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  loadTypePayments(): void {
    this.typePaymentService.getAll().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.typePayments = response.data;
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' },
            ...this.typePayments.map(tp => ({ label: tp.description, value: tp.id }))
          ];
        } else {
          console.warn('Resposta da API de tipos de pagamento não tem a estrutura esperada:', response);
          this.typePayments = [];
          this.typePaymentDropdownOptions = [
            { label: 'Selecione um método de pagamento', value: '' }
          ];
        }
      },
      error: (error) => {
        console.error('Erro ao carregar tipos de pagamento:', error);
        this.typePayments = [];
        this.typePaymentDropdownOptions = [
          { label: 'Selecione um método de pagamento', value: '' }
        ];
      }
    });
  }

  loadCustomers(search: string): void {
    this.customerService.getAll(search, true, 1).subscribe({
      next: (data) => {
        if (data && data.data) {
          this.customers = data.data;
          this.customerDropdownOptions = [
            { label: 'Selecione um cliente', value: '' },
            ...this.customers.map(c => ({ label: c.fullName, value: c.id }))
          ];
        } else {
          this.customers = [];
          this.customerDropdownOptions = [
            { label: 'Selecione um cliente', value: '' }
          ];
        }
      },
      error: (error) => {
        this.customers = [];
        this.customerDropdownOptions = [
          { label: 'Selecione um cliente', value: '' }
        ];
      }
    });
  }

  onCustomerSearchInput(event: any): void {
    const value = (event?.target?.value ?? '').trim();
    this.customerSearchTerm = value;
    if (this.customerSearchTerm.length >= 2) {
      this.loadCustomers(this.customerSearchTerm);
    } else {
      // Limpa para evitar lista gigante
      this.customerDropdownOptions = [
        { label: 'Selecione um cliente', value: '' }
      ];
    }
  }

  searchCustomers(): void {
    this.loadCustomers(this.customerSearchTerm.trim());
  }

  onCustomerFilter(event: any): void {
    const query: string = (event?.filter ?? '').trim();
    if (this.customerFilterTimer) {
      clearTimeout(this.customerFilterTimer);
    }
    this.customerFilterTimer = setTimeout(() => {
      if (query.length >= 2) {
        this.loadCustomers(query);
      } else {
        this.customerDropdownOptions = [
          { label: 'Selecione um cliente', value: '' }
        ];
      }
    }, 300);
  }

  loadRevenueTypes(): void {
    this.revenueTypeService.getActive().subscribe({
      next: (response) => {
        if (response && response.data) {
          this.revenueTypes = response.data;
          this.revenueTypeDropdownOptions = [
            { label: 'Selecione um tipo de receita', value: '' },
            ...this.revenueTypes.map(rt => ({ label: rt.description, value: rt.id }))
          ];
        } else {
          console.warn('Resposta da API de tipos de receita não tem a estrutura esperada:', response);
          this.revenueTypes = [];
          this.revenueTypeDropdownOptions = [
            { label: 'Selecione um tipo de receita', value: '' }
          ];
        }
      },
      error: (error) => {
        console.error('Erro ao carregar tipos de receita:', error);
        this.revenueTypes = [];
        this.revenueTypeDropdownOptions = [
          { label: 'Selecione um tipo de receita', value: '' }
        ];
      }
    });
  }

  loadBankAccounts(): void {
    this.bankAccountService.list('', true, 1).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.bankAccounts = [
            { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' },
            ...response.data
          ];
        } else {
          console.warn('Resposta da API de contas bancárias não tem a estrutura esperada:', response);
          this.bankAccounts = [
            { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' }
          ];
        }
      },
      error: (error) => {
        console.error('Erro ao carregar contas bancárias:', error);
        this.bankAccounts = [
          { id: 0, active: true, accountName: 'Selecione uma conta bancária', initialBalance: 0, balanceDate: '' }
        ];
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    const formValue = this.form.value;
    
    const financialDto: CreateFinancialDto = {
      date: formValue.dueDate,
      occurence: formValue.occurrence === 'Única' ? 1 : 2,
      description: formValue.description,
      installments: this.installments.length > 0 ? this.installments.length : 1,
      total: formValue.amount,
      typePaymentId: Number(formValue.paymentMethod),
      recipeId: Number(formValue.revenueType),
      customerId: Number(formValue.customer),
      barCode: formValue.barcode,
      observations: formValue.notes || '',
      fee: formValue.interest,
      penalty: formValue.fine,
      bankAccountId: Number(formValue.bankAccount)
    };

    if (formValue.occurrence !== 'Única' && this.installments && this.installments.length > 0) {
      financialDto.installmentsDetails = this.installments.map(inst => ({
        dueDate: inst.date,
        amount: inst.amount,
        status: inst.paid ? 'paid' : '',
        paymentMethod: Number(inst.paymentMethod)
      }));
    }

    console.log(financialDto);

    this.financialService.create(financialDto).subscribe({
      next: () => {
        this.router.navigate(['/app/financial']);
      },
      error: (error) => {
        console.error('Error creating financial:', error);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/app/financial']);
  }

  openParcelModal() {
    // Se firstInstallmentDate não estiver preenchido, preenche com dueDate
    if (!this.form.get('firstInstallmentDate')?.value) {
      this.form.get('firstInstallmentDate')?.setValue(this.form.get('dueDate')?.value);
    }
    this.showParcelModal = true;
  }
}